/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement } from "react"
import { Box, List, ListItemButton, ListItemText, Tooltip, Typography } from "@mui/material"
import StarIcon from "@mui/icons-material/Star"
import {
  dispositionHasReviewerContent,
  PcgExamPreviewEvidence,
  PcgRecordingDisposition,
  PcgReviewRecordingBundle,
  recordingSiteLabel,
} from "@medaica/common/services/medaica-api-service/pcg-review-types"

/**
 * Review-state colors per the handoff display contract: grey unreviewed,
 * blue note-without-decision, green accepted, red rejected; the ACTIVE
 * recording is marked white in the (dark-themed) prototype — in the light
 * portal theme we keep the state color and add a dark ring instead — a
 * deliberate deviation pending a portal-theme design pass.
 */
const dotColor = (disposition: PcgRecordingDisposition | null): string => {
  if (disposition?.decision === "accepted") {
    return "#22c55e" // green
  }
  if (disposition?.decision === "rejected") {
    return "#ef4444" // red
  }
  if (dispositionHasReviewerContent(disposition)) {
    return "#3b82f6" // blue — reviewer observations without an Accept/Reject decision
  }
  return "#9ca3af" // grey — unreviewed
}

const pointLabel = (recording: PcgReviewRecordingBundle): string => recordingSiteLabel(recording)

const stateLabel = (recording: PcgReviewRecordingBundle): string => {
  const decision = recording.disposition?.decision
  const hasNote = dispositionHasReviewerContent(recording.disposition)
  const base = decision === "accepted" ? "Accepted" : decision === "rejected" ? "Rejected" : "Unreviewed"
  return hasNote ? `${base} · Note` : base
}

/**
 * Panel-candidate hint from the server's _exam_preview_evidence: reasons for
 * this recording plus a star on the recommended starting recording.
 */
const previewHint = (
  previewEvidence: PcgExamPreviewEvidence | null | undefined,
  auscultationId: string
): { recommended: boolean; reasons: string[] } => ({
  recommended: previewEvidence?.recommendedAuscultationId === auscultationId,
  reasons: previewEvidence?.panelCandidates?.[auscultationId] ?? [],
})

const Dot = ({
  recording,
  active,
  onClick,
}: {
  recording: PcgReviewRecordingBundle
  active: boolean
  onClick: () => void
}): ReactElement => (
  <Tooltip title={`${pointLabel(recording)} · ${stateLabel(recording)}`}>
    <Box
      component="button"
      aria-label={`${pointLabel(recording)} — ${stateLabel(recording)}`}
      aria-current={active}
      onClick={onClick}
      sx={{
        width: 12,
        height: 12,
        borderRadius: "999px",
        border: "1px solid rgba(0,0,0,0.25)",
        backgroundColor: dotColor(recording.disposition),
        cursor: "pointer",
        padding: 0,
        outline: active ? "2px solid #0f172a" : "none",
        outlineOffset: "2px",
      }}
    />
  </Tooltip>
)

/**
 * Recording navigation rail: a plain list labelled by each recording's site
 * name. This is the navigation surface for exams the anatomical body map
 * cannot draw (lung points, `other`, unlabeled recordings).
 */
const RecordingRail = ({
  recordings,
  selectedAuscultationId,
  onSelect,
  previewEvidence = null,
}: {
  recordings: PcgReviewRecordingBundle[]
  selectedAuscultationId: string | null
  onSelect: (auscultationId: string) => void
  previewEvidence?: PcgExamPreviewEvidence | null
}): ReactElement => {
  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1.25} px={1} py={1}>
        {recordings.map((recording) => (
          <Dot
            key={recording.auscultationId}
            recording={recording}
            active={recording.auscultationId === selectedAuscultationId}
            onClick={() => onSelect(recording.auscultationId)}
          />
        ))}
        {recordings.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            No heart recordings in this exam
          </Typography>
        )}
      </Box>
      <List dense disablePadding>
        {recordings.map((recording, index) => {
          const hint = previewHint(previewEvidence, recording.auscultationId)
          return (
            <ListItemButton
              key={recording.auscultationId}
              selected={recording.auscultationId === selectedAuscultationId}
              onClick={() => onSelect(recording.auscultationId)}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "999px",
                  backgroundColor: dotColor(recording.disposition),
                  mr: 1.5,
                  flexShrink: 0,
                }}
              />
              <ListItemText
                primary={
                  <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
                    {`${index + 1}. ${pointLabel(recording)}`}
                    {hint.recommended && (
                      <Tooltip
                        title={`Suggested starting recording${
                          hint.reasons.length > 0 ? ` — ${hint.reasons.join(", ")}` : ""
                        }`}
                      >
                        <StarIcon sx={{ fontSize: 14, color: "#f59e0b" }} />
                      </Tooltip>
                    )}
                  </Box>
                }
                secondary={
                  // Reconciled guide convention: absence of a guide row = pending
                  `${stateLabel(recording)} · guide ${recording.guide?.status ?? "pending"}` +
                  (recording.recordingDuration ? ` · ${Math.round(recording.recordingDuration)}s` : "")
                }
              />
            </ListItemButton>
          )
        })}
      </List>
    </Box>
  )
}

export default RecordingRail
export { dotColor, stateLabel }
